import { Injectable, inject } from '@angular/core'
import { environment } from '../../environments/environment'
import { DownloadCsvResponse, ProductCsvTemplate } from '../types/csvTemplates'
import { catchError, firstValueFrom, map, Observable } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { ErrorService } from './error.service'
import { CommonService } from './common.service'

@Injectable({
  providedIn: 'root',
})
export class ProductCsvTemplatesService {
  private http = inject(HttpClient)
  private errorService = inject(ErrorService)
  private commonService = inject(CommonService)

  private URL = environment.baseUrl + 'product-csv-templates'

  /**
   * Creates a csv template
   * @param {ProductCsvTemplate} template The template data
   * @returns {Observable<void>}
   */
  create(template: ProductCsvTemplate): Observable<void> {
    return this.http.post<void>(this.URL, template).pipe(
      map((res: void) => {
        return res
      }),
      catchError(this.errorService.handleError),
    )
  }

  /**
   * Gets all csv templates for a company
   * @returns {Observable<Array<ProductCsvTemplate>>} The csv templates found
   */
  getAll(): Observable<Array<ProductCsvTemplate>> {
    return this.http
      .get<Array<ProductCsvTemplate>>(this.URL)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Gets a csv template by id
   * @param {string} id The template id
   * @returns {Observable<ProductCsvTemplate>} Result
   */
  getById(id: string): Observable<ProductCsvTemplate> {
    return this.http
      .get<ProductCsvTemplate>(this.URL + `/${id}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends the request to update a csv template
   * @param {string} id The template id
   * @param {ProductCsvTemplate} updates The template updates
   * @returns {Observable<void>}
   */
  edit(id: string, updates: ProductCsvTemplate): Observable<void> {
    return this.http
      .put<void>(this.URL + `/${id}`, updates)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends the request to delete a csv template
   * @param {string} id The template id
   * @returns {Observable<void>}
   */
  delete(id: string): Observable<void> {
    return this.http
      .delete<void>(this.URL + `/${id}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Downloads a csv template by id
   * @param {string} id The template id
   * @returns {Observable<DownloadCsvResponse>} Base64 result
   */
  download(id: string): Observable<DownloadCsvResponse> {
    return this.http
      .get<DownloadCsvResponse>(this.URL + `/${id}/download`)
      .pipe(catchError(this.errorService.handleError))
  }

  async downloadFiles(id: string, name: string): Promise<void> {
    const result = await firstValueFrom(this.download(id))
    const files = [
      { data: result.templateCsv, name: `${name}.csv` },
      { data: result.ingredientsCsv, name: `${name}-product ingredients.csv` },
      { data: result.resinsCsv, name: `${name}-product resins.csv` },
      { data: result.componentsCsv, name: `${name}-product components.csv` },
    ].filter((file) => !!file.data)

    for (const [index, file] of files.entries()) {
      // Space downloads so the browser can handle multiple files.
      if (index) await new Promise<void>((resolve) => setTimeout(resolve, 1000))
      this.commonService.downloadBase64File(file.data, file.name, 'text/csv')
    }
  }
}
